import { calendarEventTypeSchema, dateStringValidation, deadlineDismissalActionSchema, deadlineSourceSchema, } from '@teo/types';
import { z } from 'zod';
import { clearable } from './clearable.js';
import { displayId, entityInputSchema, entityOutputSchema } from './common.js';
const baseEventFields = z.object({
    entityInput: entityInputSchema,
    title: z.string().min(1).max(255).describe('What the entry is, as the calendar shows it.'),
    description: z.string().max(2000).optional().describe('More detail. Omit for none.'),
});
const dueDate = dateStringValidation.describe('The day the task or expiration is due.');
const taskEventSchema = baseEventFields.extend({
    eventType: z.literal('task'),
    dueDate,
});
const expirationEventSchema = baseEventFields.extend({
    eventType: z.literal('expiration'),
    dueDate,
});
const timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM in 24-hour time');
const scheduledEventSchema = baseEventFields.extend({
    eventType: z.literal('event'),
    startDate: dateStringValidation.describe('The day the event starts, as YYYY-MM-DD.'),
    startTime: timeOfDay
        .optional()
        .describe('The local time the event starts, as HH:MM in 24-hour time. Omit for all day.'),
    endDate: dateStringValidation
        .optional()
        .describe('The day the event ends, as YYYY-MM-DD. Omit for an event on one day.'),
    endTime: timeOfDay
        .optional()
        .describe('The local time the event ends, as HH:MM in 24-hour time. Omit for none.'),
});
export const createCalendarEventSchema = z.discriminatedUnion('eventType', [
    taskEventSchema,
    expirationEventSchema,
    scheduledEventSchema,
]);
export const updateCalendarEventSchema = z.object({
    eventId: displayId,
    title: z.string().min(1).max(255).optional().describe('The new title. Omit to keep it.'),
    description: clearable(z.string().max(2000)).describe('The new detail. Omit to keep it, or send null to remove it.'),
    dueDate: clearable(dateStringValidation).describe('The new due day of a task or expiration. Omit to keep it, or send null to remove it.'),
    startDate: clearable(dateStringValidation).describe('The new start day of a scheduled event, as YYYY-MM-DD. Omit to keep it.'),
    startTime: clearable(timeOfDay).describe('The new local start time, as HH:MM in 24-hour time. Omit to keep it, or send null for all day.'),
    endDate: clearable(dateStringValidation).describe('The new end day of a scheduled event, as YYYY-MM-DD. Omit to keep it, or send null to remove it.'),
    endTime: clearable(timeOfDay).describe('The new local end time, as HH:MM in 24-hour time. Omit to keep it, or send null to remove it.'),
});
export const deleteCalendarEventSchema = z.object({
    eventId: displayId,
});
export const completeCalendarEventSchema = z.object({
    eventId: displayId,
});
export const reopenCalendarEventSchema = z.object({
    eventId: displayId,
});
export const dismissCalendarItemSchema = z.object({
    eventId: displayId,
    action: deadlineDismissalActionSchema.describe('completed if the work is done; dismissed if it needs no action.'),
});
export const reopenCalendarItemSchema = z.object({
    eventId: displayId,
});
export const calendarEventOutputSchema = z.object({
    displayId,
    eventType: calendarEventTypeSchema,
    title: z.string(),
    description: z.string().nullable(),
    dueDate: z.string().nullable(),
    startDate: z.string().nullable(),
    startTime: z.string().nullable(),
    endDate: z.string().nullable(),
    endTime: z.string().nullable(),
    completedAt: z.string().nullable(),
    completedByUserName: z.string().nullable(),
    createdByUserName: z.string(),
    createdByUserId: displayId,
    entity: entityOutputSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createCalendarEventOutputSchema = calendarEventOutputSchema;
export const calendarStatusSchema = z
    .enum(['all', 'active', 'completed', 'overdue'])
    .describe('Which entries to include, by state. `overdue` means past its date and not yet done. `all` does not filter.');
export const getCalendarSchema = z.object({
    fromDate: dateStringValidation.describe('First day of the range, inclusive.'),
    toDate: dateStringValidation.describe('Last day of the range, inclusive.'),
    entityInput: entityInputSchema.optional(),
    types: z
        .array(z.string())
        .optional()
        .describe('Keep only these kinds of entry. Omit for every kind.'),
    status: calendarStatusSchema.default('all'),
    search: z.string().optional().describe('Keep only entries whose title matches this text.'),
    includeDismissed: z.boolean().default(false).describe('Include entries somebody has dismissed.'),
    limit: z.number().min(1).max(200).default(50).describe('How many entries to return.'),
    offset: z.number().min(0).default(0).describe('How many entries to skip, for paging.'),
});
const calendarItemEntitySchema = z.object({
    ownershipId: z.number().nullable(),
    portfolioId: z.number().nullable(),
    propertyId: z.number().nullable(),
});
const dismissalSchema = z.object({
    action: deadlineDismissalActionSchema,
    actionAt: z.string(),
});
export const calendarEventItemSchema = z.object({
    kind: z.literal('event'),
    displayId: z.string(),
    eventType: calendarEventTypeSchema,
    title: z.string(),
    description: z.string().nullable(),
    effectiveDate: z.string(),
    startDate: z.string().nullable(),
    startTime: z.string().nullable(),
    endDate: z.string().nullable(),
    endTime: z.string().nullable(),
    status: z.enum(['active', 'completed', 'overdue']),
    completedAt: z.string().nullable(),
    createdByUserName: z.string(),
    entityName: z.string(),
    entity: calendarItemEntitySchema,
    dismissal: dismissalSchema.nullable(),
});
export const calendarDeadlineItemSchema = z.object({
    kind: z.literal('deadline'),
    displayId: z.string(),
    source: deadlineSourceSchema,
    title: z.string(),
    description: z.string().nullable(),
    effectiveDate: z.string(),
    status: z.enum(['active', 'completed', 'overdue']),
    entityType: z.string(),
    entityDisplayId: z.string(),
    entityName: z.string(),
    entity: calendarItemEntitySchema,
    dismissal: dismissalSchema.nullable(),
});
export const calendarItemSchema = z.discriminatedUnion('kind', [
    calendarEventItemSchema,
    calendarDeadlineItemSchema,
]);
export const getCalendarOutputSchema = z.object({
    items: z.array(calendarItemSchema),
    total: z.number(),
});
//# sourceMappingURL=calendar.js.map